import { BePage, BeRow, BeSeparator, BeTextControl, BeToolbar, BeToolbarButton, type BeToolbarProps } from "@beos-css/react/index.ts";
import { useState } from "react";
import { bemailIcons as mail, netpositiveIcons as net } from "../../icons/toolbar-art.ts";

// TMailWindow::BuildButtonBar, reading window. BeMail's gap is 10, which is off the spacing scale.
function MailBar(props: Omit<BeToolbarProps, "aria-label" | "children">) {
	return (
		<BeToolbar aria-label="Mail" gap="item" equal {...props}>
			<BeToolbarButton art={mail.new}>New</BeToolbarButton>
			<BeSeparator />
			<BeToolbarButton art={mail.reply}>Reply</BeToolbarButton>
			<BeToolbarButton art={mail.forward}>Forward</BeToolbarButton>
			<BeToolbarButton art={mail.print}>Print</BeToolbarButton>
			<BeToolbarButton art={mail.trash}>Trash</BeToolbarButton>
			<BeSeparator />
			<BeToolbarButton art={mail.next}>Next</BeToolbarButton>
			<BeToolbarButton art={mail.previous}>Previous</BeToolbarButton>
			<BeToolbarButton art={mail.inbox}>Inbox</BeToolbarButton>
			<BeToolbarButton art={mail.mail}>Mail</BeToolbarButton>
			<BeSeparator />
		</BeToolbar>
	);
}

// URLView::InitData lays res/toolbar out right to left
function NetPositiveBar() {
	// 0 = back only, 1 = back and forward, 2 = forward only
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);

	return (
		<BeToolbar aria-label="Navigation">
			<BeTextControl grow aria-label="Location" defaultValue="http://www.be.com/" />
			<BeToolbarButton art={net.back} disabled={page === 2} onClick={() => setPage((p) => p + 1)}>
				Back
			</BeToolbarButton>
			<BeToolbarButton art={net.forward} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
				Forward
			</BeToolbarButton>
			<BeToolbarButton art={loading ? net.stopAnimated : net.stop} disabled={!loading} onClick={() => setLoading(false)}>
				Stop
			</BeToolbarButton>
			<BeToolbarButton art={net.reload} onClick={() => setLoading(true)}>
				Reload
			</BeToolbarButton>
			<BeToolbarButton art={net.search}>Search</BeToolbarButton>
			<BeToolbarButton art={net.home}>Home</BeToolbarButton>
			<BeToolbarButton art={downloading ? net.downloadsAnimated : net.downloads} onClick={() => setDownloading((d) => !d)}>
				Downloads
			</BeToolbarButton>
		</BeToolbar>
	);
}

export function ToolbarDemo() {
	return (
		<section id="demo-toolbar">
			<h2>Toolbar (BeToolbar, BeToolbarButton)</h2>
			<p>
				A row of controls acting as one bar; each button is its own artwork, redrawn for hover, press and disabled. Hover and press the
				buttons, and Tab through a bar — every item is its own stop, as in BeOS.
			</p>
			<p>
				<code>equal</code> — BeMail: every button takes the width of the widest. Tab to a button to see its label underlined.
			</p>
			<BePage className="be-panel" pad={1}>
				<MailBar />
			</BePage>
			<p>
				<code>equal labels=&#123;false&#125;</code> — BeMail, icons only: each label becomes a tooltip. Hover a button, or Tab to one to see
				its focus rectangle and tooltip.
			</p>
			<BePage className="be-panel" pad={1}>
				<MailBar labels={false} />
			</BePage>
			<p>
				Default — NetPositive: items keep their own widths, and the field takes the leftover space. Back and Forward step through three
				pages, Reload animates Stop until you press it, and Downloads toggles its own animation.
			</p>
			<BePage className="be-panel" pad={1}>
				<NetPositiveBar />
			</BePage>
			<p>
				<code>BeToolbarButton</code> states — each is its own bitmap; a state the art lacks draws the enabled one.
			</p>
			<BePage className="be-panel" pad={1}>
				<BeRow gap="section">
					<BeToolbarButton art={mail.reply}>Enabled</BeToolbarButton>
					<BeToolbarButton art={mail.reply} className="is-rollover">
						Rollover
					</BeToolbarButton>
					<BeToolbarButton art={mail.reply} className="is-pressed">
						Pressed
					</BeToolbarButton>
					<BeToolbarButton art={mail.reply} className="is-focus">
						Focused
					</BeToolbarButton>
					<BeToolbarButton art={mail.reply} disabled>
						Disabled
					</BeToolbarButton>
				</BeRow>
			</BePage>
		</section>
	);
}
